import { Button, Title, useMantineTheme } from '@mantine/core';
import { useState } from 'react';
import {
	useTranslationInvestor,
	useTranslationMipManage,
} from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import {
	GetKYCInvestorInstanceRes,
	KycWorkflowSettingResponseDto,
} from '@dealstack/shared/lib/generated';
import { FlexExtended } from '@dealstack/frontend/shared/components/Flex/FlexExtended';
import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon/Icon';
import { useDeclarations } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ManageKYC/ReviewKycWorkflow/component/StepContent/component/Declarations/useDeclarations';
import { DeclarationsEditForm } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ManageKYC/ReviewKycWorkflow/component/StepContent/component/Declarations/DeclarationsEditForm';
import { DetailRow } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ManageKYC/component/DetailRow';

interface Props {
	currentInvestor?: GetKYCInvestorInstanceRes;
	kycSetting?: KycWorkflowSettingResponseDto;
	isReadOnly?: boolean;
}

export const Declarations = ({ currentInvestor, kycSetting, isReadOnly = false }: Props) => {
	const theme = useMantineTheme();
	const tMipManage = useTranslationMipManage();
	const tInvestor = useTranslationInvestor();
	const [isEditing, setIsEditing] = useState(false);

	const declarations = useDeclarations(tMipManage, tInvestor, currentInvestor, {
		isBeneficialOwnershipRequired: kycSetting?.isBeneficialOwnershipRequired,
		isSourceOfFundsRequired: kycSetting?.isSourceOfFundsRequired,
		isPepRequired: kycSetting?.isPepRequired,
	});

	const canEdit = !isReadOnly;

	return (
		<FlexExtended direction="column" p="1rem" w="100%" h="100%" verticalScroll>
			<FlexExtended align="center" justify="space-between" mb="1rem">
				<Title order={5} c={theme.colors.dark[8]} tt="initial" fw={600}>
					{tMipManage('kyc.modal.declaration')}
				</Title>
				{canEdit && !isEditing && (
					<Button
						variant="subtle"
						color="dark"
						size="sm"
						leftSection={<Icon variant="pencil" size="1rem" />}
						onClick={() => setIsEditing(true)}
					>
						{tMipManage('kyc.declarations.edit.button')}
					</Button>
				)}
			</FlexExtended>
			{isEditing ? (
				<DeclarationsEditForm
					currentInvestor={currentInvestor}
					kycSetting={kycSetting}
					onCancel={() => setIsEditing(false)}
					onSaved={() => setIsEditing(false)}
				/>
			) : (
				declarations.map((declaration) => (
					<DetailRow key={declaration.id} label={declaration.label} mt="1.125rem">
						{declaration.value}
					</DetailRow>
				))
			)}
		</FlexExtended>
	);
};
